import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import HistoryIcon from '@mui/icons-material/History';
import type { TextFieldProps } from '@mui/material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useSnackbar } from 'notistack';
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useUser } from '../allauth/auth/hooks';
import { resolveApiBaseURL } from '../config/apiHost';
import { useSessionSchedule } from '../hooks/useSessionSchedule';
import type { ScheduleRepeats } from '../types/sessionSchedule';
import {
  flattenParticipantsToOptions,
  groupedSlotsWithOthersProposals,
  isGroupedSlotConfirmable,
  myPreferencesFromOptions,
  mySelectionCanonicalIds,
  sameSlot,
  sessionSelectChoices,
  toPreferenceInputs,
} from '../utils/sessionScheduleClient';
import {
  formatSlotLabel,
  formatSlotLabelFromDayjs,
  SLOT_DATETIME_FORMAT,
} from '../utils/sessionScheduleFormat';

const DURATION_OPTIONS = [20, 25, 30, 35, 40, 45, 50, 55, 60];

type PreferenceDateTimeTextFieldProps = TextFieldProps & {
  when: Dayjs | null;
  durationMinutes: number;
};

const PreferenceDateTimeTextField = forwardRef<HTMLDivElement, PreferenceDateTimeTextFieldProps>(
  function PreferenceDateTimeTextField(props, ref) {
    const {
      when,
      durationMinutes,
      value: _pickerValue,
      placeholder: _formatPlaceholder,
      ...rest
    } = props;
    const displayValue =
      when != null ? formatSlotLabelFromDayjs(when, durationMinutes) : '';
    return (
      <TextField
        {...rest}
        size="small"
        ref={ref}
        value={displayValue}
        label="Date and time"
        placeholder="Select a date and time"
      />
    );
  },
);

type PreferenceDateTimePickerProps = {
  when: Dayjs | null;
  durationMinutes: number;
  disabled?: boolean;
  position: number;
  onWhenChange: (when: Dayjs | null) => void;
};

/** Opens the calendar on field focus/click (MUI v7 only opens via the icon by default). */
const PreferenceDateTimePicker: React.FC<PreferenceDateTimePickerProps> = ({
  when,
  durationMinutes,
  disabled,
  position,
  onWhenChange,
}) => {
  const [open, setOpen] = useState(false);
  // Popper restores focus to the field after close; ignore that focus so we don't reopen.
  const ignoreFocusOpenRef = useRef(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    ignoreFocusOpenRef.current = true;
    setOpen(false);
    window.setTimeout(() => {
      ignoreFocusOpenRef.current = false;
    }, 100);
  };

  const openFromField = () => {
    if (disabled || ignoreFocusOpenRef.current) {
      return;
    }
    setOpen(true);
  };

  return (
    <DateTimePicker
      format={SLOT_DATETIME_FORMAT}
      value={when}
      disablePast
      disabled={disabled}
      minDateTime={dayjs()}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      onChange={(value) => {
        const next = value != null && value.isBefore(dayjs()) ? null : value;
        onWhenChange(next);
      }}
      slots={{ textField: PreferenceDateTimeTextField }}
      slotProps={{
        textField: {
          when,
          durationMinutes,
          fullWidth: true,
          onClick: openFromField,
          onFocus: openFromField,
          inputProps: {
            'data-attr': `session-schedule_preference-${position}`,
          },
        } as PreferenceDateTimeTextFieldProps,
      }}
    />
  );
};

const MIN_PREFERENCE_ROWS = 2;

type PreferenceRow = { when: Dayjs | null; duration: number; repeats: ScheduleRepeats };

function emptyPreferenceRow(duration: number): PreferenceRow {
  return { when: null, duration, repeats: null };
}

function toOrdinal(number: string | number) {
  const pr = new Intl.PluralRules('en-US', { type: 'ordinal' });
  const suffixes = {
    one: 'st',
    two: 'nd',
    few: 'rd',
    other: 'th'
  };

  const rule = pr.select(Number(number));
  const suffix = suffixes[rule as keyof typeof suffixes];

  return <span>{number}<small>{suffix}</small></span>;
}

function preferenceChoiceLabel(rank: number, ordinal: boolean = false): React.ReactNode {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 22,
        height: 22,
        borderRadius: '50%',
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      {ordinal ? toOrdinal(rank) : rank}
    </Box>
  );
}

const PREFERENCES_SAVED_MESSAGE =
  'Other parties will be notified to find a time that works.';

const SessionSchedulePage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const viewer = useUser();
  const viewerUserId = viewer?.id as number | undefined;
  const { id: lessonPlanId, assignment_id: assignmentIdParam } = useParams();
  const scheduleSource = useMemo(() => {
    if (assignmentIdParam) {
      return { kind: 'assignment' as const, assignmentId: assignmentIdParam };
    }
    if (lessonPlanId) {
      return { kind: 'lessonPlan' as const, lessonPlanId };
    }
    return undefined;
  }, [assignmentIdParam, lessonPlanId]);

  const {
    data,
    loading,
    error,
    savePreferences,
    savingPreferences,
    updatingSelection,
    toggleAvailability,
    confirmSchedule,
    confirming,
    declineConfirmedSchedule,
    declining,
    selectSession,
    assignmentId,
    sessionNumber,
  } = useSessionSchedule(scheduleSource);

  const [prefRows, setPrefRows] = useState<PreferenceRow[]>(() =>
    Array.from({ length: MIN_PREFERENCE_ROWS }, () => emptyPreferenceRow(30)),
  );
  const [confirmingSlotId, setConfirmingSlotId] = useState<number | null>(null);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);

  const allOptions = useMemo(() => flattenParticipantsToOptions(data), [data]);
  const sessionChoices = useMemo(() => sessionSelectChoices(data), [data]);
  const participantsById = data?.participants ?? {};
  const resolveParticipantUser = (userId: number) =>
    participantsById[String(userId)]?.user ?? null;
  const myPreferences = useMemo(() => {
    if (!viewerUserId) {
      return [];
    }
    return myPreferencesFromOptions(allOptions, viewerUserId);
  }, [allOptions, viewerUserId]);

  useEffect(() => {
    const defaultDuration = data?.duration_default ?? 30;
    const rows: PreferenceRow[] = myPreferences.map((pref) => ({
      when: dayjs(pref.starts_at),
      duration: pref.duration_minutes,
      repeats: pref.repeats,
    }));
    while (rows.length < MIN_PREFERENCE_ROWS) {
      rows.push(emptyPreferenceRow(defaultDuration));
    }
    setPrefRows(rows);
  }, [myPreferences, data?.duration_default, sessionNumber]);

  const mySelectionIds = useMemo(() => {
    if (!viewerUserId) {
      return new Set<number>();
    }
    return new Set(mySelectionCanonicalIds(allOptions, viewerUserId));
  }, [allOptions, viewerUserId]);

  const myOptionIds = useMemo(
    () => new Set(myPreferences.map((p) => p.option_id)),
    [myPreferences],
  );

  const othersGroupedSlots = useMemo(
    () => groupedSlotsWithOthersProposals(allOptions, myOptionIds),
    [allOptions, myOptionIds],
  );

  if (loading && !data) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const backLink = lessonPlanId
    ? `/lesson-plan/${lessonPlanId}`
    : assignmentIdParam || assignmentId
      ? `/program-progress/${assignmentIdParam ?? assignmentId}`
      : '/';

  if (data?.disabled) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">Scheduling is not available for this program.</Alert>
        <Button component={Link} to={backLink} sx={{ mt: 2 }}>
          Back
        </Button>
      </Container>
    );
  }

  const confirmedLabel =
    data?.status === 'confirmed' && data.scheduled_starts_at
      ? formatSlotLabel(data.scheduled_starts_at, data.scheduled_duration_minutes ?? 30)
      : '';
  const icalUrl =
    data?.status === 'confirmed' && assignmentId && sessionNumber != null
      ? `${resolveApiBaseURL()}/api/payment-assignments/${assignmentId}/sessions/${sessionNumber}/schedule/ical/`
      : null;

  const handleSavePreferences = async () => {
    const now = dayjs();
    const fromRows = prefRows
      .filter((row) => row.when && !row.when.isBefore(now))
      .map((row) => ({
        starts_at: row.when!.toISOString(),
        duration_minutes: row.duration,
        repeats: row.repeats,
      }));
    const checkedOthers = othersGroupedSlots
      .filter((slot) => mySelectionIds.has(slot.canonical_option_id))
      .filter((slot) => !fromRows.some((row) => sameSlot(row, slot)))
      .map((slot) => ({
        starts_at: slot.starts_at,
        duration_minutes: slot.duration_minutes,
        repeats: null,
      }));
    const fullSet = [...fromRows, ...checkedOthers];
    if (fullSet.length === 0) {
      enqueueSnackbar('Add at least one date and time to propose.', { variant: 'warning' });
      return;
    }
    const result = await savePreferences(toPreferenceInputs(fullSet));
    if (result.success) {
      enqueueSnackbar(PREFERENCES_SAVED_MESSAGE, { variant: 'success' });
    } else {
      enqueueSnackbar(result.error, { variant: 'error' });
    }
  };

  const updatePrefRow = (index: number, patch: Partial<PreferenceRow>) => {
    setPrefRows((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addPrefRow = () => {
    setPrefRows((rows) => [...rows, emptyPreferenceRow(data?.duration_default ?? 30)]);
  };

  const handleDeclineConfirmed = async () => {
    setDeclineDialogOpen(false);
    const result = await declineConfirmedSchedule();
    if (result.success) {
      enqueueSnackbar('Scheduling reopened. Others have been notified.', { variant: 'success' });
    } else {
      enqueueSnackbar(result.error, { variant: 'error' });
    }
  };

  const handleConfirmSlot = async (optionId: number, canonicalOptionId: number) => {
    setConfirmingSlotId(canonicalOptionId);
    const result = await confirmSchedule(optionId);
    setConfirmingSlotId(null);
    if (result.success) {
      enqueueSnackbar('Session time confirmed.', { variant: 'success' });
    } else {
      enqueueSnackbar(result.error, { variant: 'error' });
    }
  };



  return (
    <React.Fragment>
      <Button component={Link} to={backLink} startIcon={<ArrowBackIcon />} sx={{ mx: 2, my: 1 }}>
        Back
      </Button>
      <Container maxWidth="md" >

        {sessionNumber != null && (
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="session-schedule-session-select">Schedule your {toOrdinal(sessionNumber)} session</InputLabel>
            <Select
              labelId="session-schedule-session-select"
              label={`Schedule your ${toOrdinal(sessionNumber)} session`}
              value={sessionNumber}
              onChange={(e) => selectSession(Number(e.target.value))}
              data-attr="session-schedule_session-select"
            >
              {sessionChoices.map((session) => (
                <MenuItem key={session.key} value={session.session_number}>
                  {session.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {data?.status === 'confirmed' && confirmedLabel && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Session scheduled for {confirmedLabel}
            </Typography>
            {(data.emailed_recipient_summaries?.length ?? 0) > 0 && (
              <Typography variant="body2" color="text.secondary">
                Calendar invites were emailed to:{' '}
                {(data.emailed_recipient_summaries ?? []).map((r) => r.str).join(', ')}
              </Typography>
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }} useFlexGap>
              {icalUrl && (
                <Button
                  size="small"
                  variant="outlined"
                  href={icalUrl}
                  data-attr="session-schedule_download-ical"
                >
                  Download calendar file
                </Button>
              )}
              {data.can_decline_confirmed && (
                <Button
                  size="small"
                  color="warning"
                  variant="outlined"
                  disabled={declining}
                  onClick={() => setDeclineDialogOpen(true)}
                  data-attr="session-schedule_cant-make-confirmed"
                >
                  {declining ? 'Updating…' : "I can't make this"}
                </Button>
              )}
            </Stack>
          </Alert>
        )}

        <Dialog
          open={declineDialogOpen}
          onClose={() => !declining && setDeclineDialogOpen(false)}
          aria-labelledby="session-schedule-decline-dialog-title"
          aria-describedby="session-schedule-decline-dialog-description"
        >
          <DialogTitle id="session-schedule-decline-dialog-title">
            Reopen scheduling for this session?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="session-schedule-decline-dialog-description" component="div">
              <Typography variant="body2" paragraph sx={{ mb: 1.5 }}>
                {confirmedLabel ? (
                  <>
                    The confirmed time{' '}
                    <strong>{confirmedLabel}</strong> will be cleared for everyone on this program.
                  </>
                ) : (
                  <>The confirmed session time will be cleared for everyone on this program.</>
                )}
              </Typography>
              <Typography variant="body2" paragraph sx={{ mb: 1.5 }}>
                Scheduling will reopen so the group can pick a new time. Other participants will be
                notified, and your availability for this slot will be removed.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You can propose new times or accept someone else&apos;s suggestions after that.
              </Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeclineDialogOpen(false)}
              disabled={declining}
              color="inherit"
              data-attr="session-schedule_decline-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeclineConfirmed}
              color="warning"
              variant="contained"
              disabled={declining}
              data-attr="session-schedule_decline-confirm"
            >
              {declining ? 'Updating…' : "Yes, I can't make this"}
            </Button>
          </DialogActions>
        </Dialog>

        {othersGroupedSlots.length > 0 && <React.Fragment>
          <Typography variant="h6" gutterBottom>
            Do any of these times work for you?
          </Typography>

          <Stack spacing={0.5}>
            {othersGroupedSlots.map((slot) => {
              const selected = mySelectionIds.has(slot.canonical_option_id);
              const toggleOption =
                slot.options.find((option) => !myOptionIds.has(option.option_id))
                ?? slot.options[0];
              const proposerIds = new Set<number>();
              const peopleChips: Array<{
                user: NonNullable<typeof toggleOption.proposed_by>;
                role: 'proposer' | 'accepter';
                rank?: number | null;
              }> = [];
              for (const option of slot.options) {
                const proposer = option.proposed_by;
                if (proposer && !proposerIds.has(proposer.id)) {
                  proposerIds.add(proposer.id);
                  peopleChips.push({
                    user: proposer,
                    role: 'proposer',
                    rank: option.rank,
                  });
                }
              }
              peopleChips.sort(
                (a, b) =>
                  (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER),
              );
              for (const userId of slot.selected_by) {
                if (proposerIds.has(userId)) {
                  continue;
                }
                const user = resolveParticipantUser(userId);
                if (user) {
                  peopleChips.push({ user, role: 'accepter' });
                }
              }
              const isLastOfMine = selected && myPreferences.length <= 1;
              const showReady = isGroupedSlotConfirmable(slot);
              const showPeopleRow =
                slot.is_carry_forward || peopleChips.length > 0 || showReady;
              return (
                <Card
                  key={`${slot.starts_at}-${slot.duration_minutes}`}
                  variant="outlined"
                  sx={{
                    borderColor: isGroupedSlotConfirmable(slot) ? 'success.main' : undefined,
                    borderWidth: isGroupedSlotConfirmable(slot) ? 2 : 1,
                  }}
                >
                  <CardContent
                    sx={{
                      py: 0.75,
                      px: 1,
                      '&:last-child': { pb: 0.75 },
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" alignContent={'flex-start'} spacing={0.5}>
                      <Tooltip title={isLastOfMine ? 'Add another time before removing your last one' : ''}>
                        <span>
                          <Checkbox
                            size="small"
                            checked={selected}
                            disabled={updatingSelection || isLastOfMine}
                            onChange={(_, checked) =>
                              toggleAvailability(toggleOption, checked, myPreferences)
                            }
                            inputProps={{
                              'aria-label': 'I can make this time',
                            }}
                            data-attr="session-schedule_toggle-availability"
                            sx={{ mt: -0.25, p: 0.5, minWidth: 40, minHeight: 40 }}
                          />
                        </span>
                      </Tooltip>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                          {formatSlotLabel(slot.starts_at, slot.duration_minutes)}
                        </Typography>

                        {showPeopleRow && (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            sx={{ mt: 0.5, flexWrap: 'wrap' }}
                            useFlexGap
                          >
                            {slot.is_carry_forward && (
                              <Tooltip title="Suggested from last session">
                                <HistoryIcon
                                  fontSize="small"
                                  color="action"
                                  aria-label="Suggested from last session"
                                />
                              </Tooltip>
                            )}
                            {peopleChips.map(({ user, role, rank }) => (
                              <Chip
                                key={`${role}-${user.id}`}
                                size="small"
                                color={role === 'proposer' ? 'primary' : 'default'}
                                variant={role === 'proposer' ? 'filled' : 'outlined'}
                                avatar={
                                  <Avatar
                                    alt={user.str}
                                    src={user.img ?? undefined}
                                  />
                                }
                                label={
                                  role === 'proposer' ? (
                                    <Typography component="span" variant="body2">
                                      {user.str}{' '}
                                      <Typography component="span" variant="caption">
                                        {preferenceChoiceLabel(rank ?? 1, true)}
                                      </Typography>
                                    </Typography>
                                  ) : (
                                    user.str
                                  )
                                }
                              />
                            ))}
                            {showReady && data?.status !== 'confirmed' && (
                              <Chip
                                size="small"
                                color="success"
                                clickable
                                icon={<EventAvailableIcon fontSize="small" />}
                                label={
                                  confirmingSlotId === slot.canonical_option_id
                                    ? 'Confirming…'
                                    : 'Confirm session time'
                                }
                                disabled={
                                  loading
                                  || confirming
                                  || confirmingSlotId !== null
                                }
                                onClick={() =>
                                  handleConfirmSlot(
                                    slot.confirm_option_id,
                                    slot.canonical_option_id,
                                  )
                                }
                                data-attr="session-schedule_confirm"
                              />
                            )}
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>

          <Divider sx={{ my: 4 }} />

        </React.Fragment>

        }


        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Suggest times that work for you
          </Typography>
          <Stack spacing={2.5}>
            {prefRows.map((row, index) => {
              const position = index + 1;
              return (
                <Box key={index}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignContent={'center'}
                    alignItems="center"
                  >
                    {preferenceChoiceLabel(position)}
                    <PreferenceDateTimePicker
                      when={row.when}
                      durationMinutes={row.duration}
                      disabled={data?.status === 'confirmed'}
                      position={position}
                      onWhenChange={(nextWhen) => updatePrefRow(index, { when: nextWhen })}
                    />
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel id={`duration-${position}`}>Duration</InputLabel>
                      <Select
                        labelId={`duration-${position}`}
                        label="Duration"
                        disabled={data?.status === 'confirmed'}
                        value={row.duration}
                        onChange={(e) => updatePrefRow(index, { duration: Number(e.target.value) })}
                      >
                        {DURATION_OPTIONS.map((m) => (
                          <MenuItem key={m} value={m}>{m} min</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  <FormControlLabel
                    sx={{ mt: 0, ml: { sm: 3 } }}
                    control={
                      <Checkbox
                        size="small"
                        sx={{ m: 0, p: .5 }}
                        checked={row.repeats === 'weekly'}
                        onChange={(_, checked) => updatePrefRow(index, { repeats: checked ? 'weekly' : null })}
                        inputProps={{
                          'data-attr': `session-schedule_preference-weekly-${position}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>}
                      />
                    }
                    label={<Typography variant="caption">Available weekly</Typography>}
                  />
                </Box>
              );
            })}
          </Stack>
          <Button
            startIcon={<AddIcon />}
            sx={{ mt: 1 }}
            onClick={addPrefRow}
            disabled={data?.status === 'confirmed'}
            data-attr="session-schedule_add-preference"
          >
            Add another time
          </Button>
          <Box>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={handleSavePreferences}
              disabled={savingPreferences || data?.status === 'confirmed'}
              data-attr="session-schedule_save-preferences"
            >
              {savingPreferences ? 'Saving…' : 'Propose my times'}
            </Button>
          </Box>
        </Box>

        <Box sx={{ position: 'relative', mb: 4 }}>
          {updatingSelection && (
            <Box
              aria-busy="true"
              aria-label="Updating availability"
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(0, 0, 0, 0.45)'
                    : 'rgba(255, 255, 255, 0.72)',
              }}
            >
              <CircularProgress />
            </Box>
          )}

        </Box>
      </Container>
    </React.Fragment>

  );
};

export default SessionSchedulePage;
